// assets/custom-cart-addons.js
// <custom-cart-addons>: keeps add-on lines in step with their card on the cart page and in the cart
// drawer (sections/main-cart-items.liquid, snippets/cart-drawer.liquid).
//
// Lines belong together through the private properties written by custom-addons.js:
//   _addons_group  one id per personalised card configuration
//   _addons_role   'card' on the main line, the add-on role on each paid line
// Add-on lines are also nested under the card (parent_line_key), which makes Shopify remove them with
// the card; the rules below are still applied explicitly because nesting does not sync quantities
// or the card's own properties:
//   - card quantity changes  -> every add-on line gets the card's new quantity
//   - card removed           -> its add-on lines are removed
//   - add-on removed         -> the card's flags and "Selected add-ons" are rewritten
//   - on load                -> orphaned or mismatched lines are corrected once
// Requests run one at a time; while one runs, Dawn's own quantity/remove handlers are held back so
// two updates never race. Rendering mirrors Dawn's cart.js: the same sections are requested and
// swapped in.
if (!customElements.get('custom-cart-addons')) {
  const ROLES = ['double_thick', 'envelope_color', 'liner', 'return_address', 'guest_addresses'];
  const PARENT_ROLE = 'card';
  const DOUBLE_THICK_VALUE = 'Double Thick';
  const PROPERTY = {
    paper: 'Paper weight',
    color: 'Envelope color',
    liner: 'Envelope liner',
    returnAddress: 'Return address printing',
    name: 'Return address name',
    street: 'Return address street',
    city: 'Return address city/state/ZIP',
    guest: 'Guest address printing',
    source: 'Guest address source',
    list: 'Guest address list',
    sheet: 'Guest address sheet',
    help: 'Address list help',
    contactMethod: 'Preferred contact method',
    contactEmail: 'Contact email',
    contactPhone: 'Contact phone',
    selected: 'Selected add-ons',
    group: '_addons_group',
    role: '_addons_role',
    label: '_addons_label',
    includedColor: '_addons_included_color',
  };
  const MANAGED_KEYS = [
    PROPERTY.paper,
    PROPERTY.color,
    PROPERTY.liner,
    PROPERTY.returnAddress,
    PROPERTY.name,
    PROPERTY.street,
    PROPERTY.city,
    PROPERTY.guest,
    PROPERTY.source,
    PROPERTY.list,
    PROPERTY.sheet,
    PROPERTY.help,
    PROPERTY.contactMethod,
    PROPERTY.contactEmail,
    PROPERTY.contactPhone,
    PROPERTY.selected,
  ];

  // Cart states already reconciled in this page load (item keys + quantities); guards against loops
  // in the drawer, where every render reconnects the element
  const reconciledSignatures = new Set();

  /* ---------- services ---------- */

  const cartRoutes = () => {
    const routes = window.routes || {};
    return {
      change: routes.cart_change_url || '/cart/change.js',
      update: routes.cart_update_url || '/cart/update.js',
      cart: `${routes.cart_url || '/cart'}.js`,
    };
  };

  const postJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await response.json();
    if (json.status) throw new Error(json.description || json.message || '');
    return json;
  };

  const fetchCart = async () => {
    const response = await fetch(cartRoutes().cart, { headers: { Accept: 'application/json' } });
    return response.json();
  };

  const groupOf = (item) => item.properties?.[PROPERTY.group] || null;
  const roleOf = (item) => item.properties?.[PROPERTY.role] || null;
  const isChild = (item) => Boolean(groupOf(item)) && roleOf(item) !== PARENT_ROLE;
  const sortChildren = (items) => [...items].sort((a, b) => ROLES.indexOf(roleOf(a)) - ROLES.indexOf(roleOf(b)));
  const signatureOf = (cart) => cart.items.map((item) => `${item.key}:${item.quantity}`).join('|');

  // Card properties for the add-on lines that really exist (twin of the builder in custom-addons.js)
  const buildParentProperties = (parent, children, strings) => {
    const base = parent.properties || {};
    const properties = { ...base };
    const byRole = new Map(children.map((child) => [roleOf(child), child]));
    const has = (role) => byRole.has(role);

    properties[PROPERTY.paper] = has('double_thick') ? DOUBLE_THICK_VALUE : strings.standard;
    properties[PROPERTY.color] = has('envelope_color')
      ? byRole.get('envelope_color').properties?.[PROPERTY.color] || base[PROPERTY.color]
      : base[PROPERTY.includedColor] || strings.included;
    properties[PROPERTY.liner] = has('liner') ? strings.yes : strings.no;

    properties[PROPERTY.returnAddress] = has('return_address') ? strings.yes : strings.no;
    if (!has('return_address')) {
      delete properties[PROPERTY.name];
      delete properties[PROPERTY.street];
      delete properties[PROPERTY.city];
    }

    properties[PROPERTY.guest] = has('guest_addresses') ? strings.yes : strings.no;
    if (!has('guest_addresses')) {
      delete properties[PROPERTY.source];
      delete properties[PROPERTY.list];
      delete properties[PROPERTY.sheet];
      delete properties[PROPERTY.help];
      delete properties[PROPERTY.contactMethod];
      delete properties[PROPERTY.contactEmail];
      delete properties[PROPERTY.contactPhone];
    }

    properties[PROPERTY.selected] = children.length
      ? children.map((child) => child.properties?.[PROPERTY.label] || child.product_title).join(', ')
      : strings.none;
    return properties;
  };

  const managedEqual = (a, b) => MANAGED_KEYS.every((key) => (a[key] || '') === (b[key] || ''));

  /* ---------- controller ---------- */

  class CustomCartAddons extends HTMLElement {
    connectedCallback() {
      // Guard against re-entry: connectedCallback can fire more than once
      if (this.initialised) return;
      this.initialised = true;

      this.cartItems = this.closest('cart-items, cart-drawer-items');
      if (!this.cartItems) return;
      this.isDrawer = this.cartItems.tagName === 'CART-DRAWER-ITEMS';
      this.strings = {
        yes: this.dataset.yes || 'Yes',
        no: this.dataset.no || 'No',
        standard: this.dataset.standard || 'Standard',
        none: this.dataset.none || 'None',
        included: this.dataset.included || 'Included',
        error: this.dataset.error || window.cartStrings?.error || '',
      };
      this.busy = false;
      this.queue = Promise.resolve();

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      // Capture phase on document: runs before Dawn's cart-items / cart-remove-button handlers
      document.addEventListener('change', this.onChange, true);
      document.addEventListener('click', this.onClick, true);

      this.reconcile();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange, true);
      document.removeEventListener('click', this.onClick, true);
    }

    /* ---------- event interception ---------- */

    rowFor(target) {
      const row = target.closest?.('.cart-item');
      return row && this.cartItems.contains(row) ? row : null;
    }

    groupRows(group) {
      return [...this.cartItems.querySelectorAll('.cart-item[data-addons-group]')].filter((row) => row.dataset.addonsGroup === group);
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches?.('input.quantity__input')) return;
      const row = this.rowFor(input);
      if (!row) return;
      if (this.busy) {
        // A group request is running: hold every other quantity change in this cart back
        event.stopImmediatePropagation();
        input.value = input.getAttribute('value');
        return;
      }
      if (!row.dataset.addonsGroup) return;
      event.stopImmediatePropagation();
      if (row.dataset.addonsRole !== PARENT_ROLE) return;
      this.enqueue(() => this.changeParentQuantity(row, input));
    }

    onClick(event) {
      const button = event.target.closest?.('cart-remove-button');
      if (!button) return;
      const row = this.rowFor(button);
      if (!row) return;
      if (this.busy) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (!row.dataset.addonsGroup) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.enqueue(() => (row.dataset.addonsRole === PARENT_ROLE ? this.removeGroup(row) : this.removeChild(row)));
    }

    enqueue(task) {
      this.queue = this.queue.then(task).catch((error) => console.error('[custom-cart-addons]', error));
      return this.queue;
    }

    /* ---------- operations ---------- */

    async changeParentQuantity(row, input) {
      const key = row.dataset.addonsKey;
      const group = row.dataset.addonsGroup;
      const requested = parseInt(input.value, 10);
      const index = input.dataset.index;
      this.setBusy(true, row);
      try {
        // change.js validates the card's own quantity rules and stock; update.js would not
        let state = await postJson(cartRoutes().change, { id: key, quantity: requested, ...this.sectionsPayload() });
        const parent = state.items.find((item) => item.key === key);
        const actual = parent ? parent.quantity : 0;
        const updates = {};
        state.items
          .filter((item) => groupOf(item) === group && isChild(item) && item.quantity !== actual)
          .forEach((item) => (updates[item.key] = actual));
        if (Object.keys(updates).length) {
          state = await postJson(cartRoutes().update, { updates, ...this.sectionsPayload() });
        }
        this.render(state);
        if (parent && actual !== requested) {
          const template = window.cartStrings?.quantityError || '';
          this.showLineMessage(index, template.replace('[quantity]', actual));
        }
      } catch (error) {
        input.value = input.getAttribute('value');
        this.showError(error.message);
      } finally {
        this.setBusy(false);
      }
    }

    async removeGroup(row) {
      const updates = {};
      // Explicit zeros for every line of the group: nesting removes children with the parent on
      // Shopify's side, property-only grouping (fallback) relies on this
      this.groupRows(row.dataset.addonsGroup).forEach((groupRow) => (updates[groupRow.dataset.addonsKey] = 0));
      this.setBusy(true, row);
      try {
        const state = await postJson(cartRoutes().update, { updates, ...this.sectionsPayload() });
        this.render(state);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setBusy(false);
      }
    }

    async removeChild(row) {
      const group = row.dataset.addonsGroup;
      this.setBusy(true, row);
      try {
        let state = await postJson(cartRoutes().change, { id: row.dataset.addonsKey, quantity: 0, ...this.sectionsPayload() });
        const parent = state.items.find((item) => groupOf(item) === group && roleOf(item) === PARENT_ROLE);
        if (parent) {
          // Properties are replaced wholesale by change.js, so the full merged set is sent back
          const children = sortChildren(state.items.filter((item) => groupOf(item) === group && isChild(item)));
          const properties = buildParentProperties(parent, children, this.strings);
          state = await postJson(cartRoutes().change, {
            id: parent.key,
            quantity: parent.quantity,
            properties,
            ...this.sectionsPayload(),
          });
        }
        this.render(state);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setBusy(false);
      }
    }

    // One pass on load: children without a card go, quantities follow the card, card flags follow
    // the children. Covers paths that skip this script (no-JS update, checkout edits).
    reconcile() {
      if (!this.cartItems.querySelector('.cart-item[data-addons-group]')) return;
      this.enqueue(async () => {
        const cart = await fetchCart();
        const signature = signatureOf(cart);
        if (reconciledSignatures.has(signature)) return;
        reconciledSignatures.add(signature);

        const groups = new Map();
        cart.items.forEach((item) => {
          const group = groupOf(item);
          if (!group) return;
          if (!groups.has(group)) groups.set(group, []);
          groups.get(group).push(item);
        });

        const updates = {};
        const parentFixes = [];
        groups.forEach((items) => {
          const parent = items.find((item) => roleOf(item) === PARENT_ROLE);
          const children = sortChildren(items.filter(isChild));
          if (!parent) {
            children.forEach((child) => (updates[child.key] = 0));
            return;
          }
          children.forEach((child) => {
            if (child.quantity !== parent.quantity) updates[child.key] = parent.quantity;
          });
          const expected = buildParentProperties(parent, children, this.strings);
          if (!managedEqual(expected, parent.properties || {})) parentFixes.push({ parent, expected });
        });
        if (!Object.keys(updates).length && !parentFixes.length) return;

        this.setBusy(true);
        try {
          let state = null;
          if (Object.keys(updates).length) {
            state = await postJson(cartRoutes().update, { updates, ...this.sectionsPayload() });
          }
          for (const fix of parentFixes) {
            state = await postJson(cartRoutes().change, {
              id: fix.parent.key,
              quantity: fix.parent.quantity,
              properties: fix.expected,
              ...this.sectionsPayload(),
            });
          }
          if (state) this.render(state);
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.setBusy(false);
        }
      });
    }

    /* ---------- rendering (mirrors Dawn's cart.js) ---------- */

    sectionsToRender() {
      if (this.isDrawer) {
        return [
          { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ];
      }
      const items = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      return [
        { id: 'main-cart-items', section: items?.dataset.id, selector: '.js-contents' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
        { id: 'main-cart-footer', section: footer?.dataset.id, selector: '.js-contents' },
      ].filter((section) => section.section);
    }

    sectionsPayload() {
      return {
        sections: this.sectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      };
    }

    render(state) {
      if (!state?.sections) {
        window.location.reload();
        return;
      }
      this.sectionsToRender().forEach((section) => {
        const html = state.sections[section.section];
        const container = document.getElementById(section.id);
        if (!html || !container) return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        const target = container.querySelector(section.selector) || container;
        if (source) target.innerHTML = source.innerHTML;
      });
      const empty = state.item_count === 0;
      this.cartItems.classList.toggle('is-empty', empty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', empty);
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'custom-cart-addons', cartData: state });
      }
    }

    setBusy(on, row) {
      this.busy = on;
      this.cartItems.classList.toggle('addons-busy', on);
      row?.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.toggle('hidden', !on));
    }

    showError(message) {
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = message || this.strings.error;
    }

    showLineMessage(index, message) {
      const line = document.getElementById(`Line-item-error-${index}`) || document.getElementById(`CartDrawer-LineItemError-${index}`);
      const text = line?.querySelector('.cart-item__error-text');
      if (text) text.textContent = message;
    }
  }

  customElements.define('custom-cart-addons', CustomCartAddons);
}
